// Самообновление десктопной программы.
//
// ## Зачем этот файл
//
// Интерфейс приезжает ВНУТРИ программы, а не из сети (ПРАВИЛА.md, решение 7): у окна нет
// вкладки, которую можно перезагрузить. Значит правка, сделанная сегодня, доходит до
// финансового директора ровно одним способом — новым установщиком. Без самообновления этот
// способ называется «пришлите файл в мессенджер», и через месяц у каждого своя версия, а
// разбор «у меня такого экрана нет» съедает больше времени, чем сама правка.
//
// ## Как это выглядит по времени
//
// | Когда | Что видит человек |
// |---|---|
// | запуск | ничего: первые полминуты принадлежат работе, а не служебному запросу |
// | дальше | ничего: проверка раз в пять минут, ответ размером с одну строку |
// | нашлось | ничего: файл качается молча, человек ничего не просил |
// | скачалось | полоса внизу с кнопкой «Обновить» и значок «Есть обновление» в полосе разделов |
// | нажал значок | окно «Что нового в X»: список изменений и та же «Обновить» |
// | нажал «Обновить» | программа закрывается и открывается снова, уже новая |
// | открылась новая | один раз окно «Что нового в X» (`shared/desktop/что-нового.ts`) |
// | отложил | полоса уходит на два часа, значок остаётся: новость никуда не делась |
//
// Ставим ТОЛЬКО по нажатию. Программа при установке перезапускается, а человек может стоять
// в середине незакрытой ведомости: молча забрать у него окно — это потерянная работа.
//
// ## Чего здесь намеренно нет
//
// Сообщений об ошибке. «Не удалось проверить обновление» — новость про нашу работу, а не про
// его: чинить ему нечего, а следующий круг через пять минут разберётся сам. Правило 10
// требует называть причину и следующий шаг; здесь ни того ни другого для человека не
// существует, поэтому верный текст — молчание.

// ## Планшет: новая версия в App Store
//
// Самообновления на планшете нет и быть не может: приложение ставит магазин. Человек узнаёт о
// новой версии, только если сам зайдёт в магазин, и версии на планшетах расходятся так же, как
// расходились на компьютерах до самообновления (обещано заказчику 29.09.2026: «плашка, что
// вышла новая версия»). Поэтому на планшете программа сама спрашивает публичный ответ магазина
// (Lookup) и, если там версия выше своей, показывает ту же полосу с кнопкой «Обновить», которая
// открывает страницу приложения в магазине. Молчание при любой ошибке то же, что выше.

import { create } from 'zustand';

import { десктоп, платформа } from './index';

/** Пауза перед первой проверкой. Первый экран важнее служебного запроса. */
export const ПАУЗА_ПЕРЕД_ПЕРВОЙ_ПРОВЕРКОЙ_МС = 30_000;

/** Как часто спрашиваем сайт про новую версию. */
export const ПЕРИОД_ПРОВЕРКИ_МС = 5 * 60_000;

/** На сколько уходит полоса после «Позже». */
export const ОТСРОЧКА_МС = 2 * 60 * 60_000;

/** Номер приложения «Барк Управление» в App Store. */
export const НОМЕР_В_МАГАЗИНЕ = '6811008991';

/**
 * Страница приложения в магазине БЕЗ языка в пути: ссылка с `/ru/` открывается только там, где
 * приложение есть в российской витрине, а эта открывается в магазине любой страны.
 */
export const ССЫЛКА_НА_МАГАЗИН = `https://apps.apple.com/app/id${НОМЕР_В_МАГАЗИНЕ}`;

/** Как часто спрашиваем магазин: версии выходят раз в недели, а запрос чужой. */
export const ПЕРИОД_ПРОВЕРКИ_МАГАЗИНА_МС = 12 * 60 * 60_000;

/** На сколько уходит полоса магазина после «Позже». */
export const ОТСРОЧКА_МАГАЗИНА_МС = 24 * 60 * 60_000;

/**
 * Витрины, в которых спрашиваем: приложение отвечает только там, где оно продаётся. Заказчик в
 * России, финансовый директор во Вьетнаме; `us` запасная, на случай, если две первые молчат.
 */
const ВИТРИНЫ_МАГАЗИНА = ['ru', 'vn', 'us'] as const;

/** Сколько ждём ответа магазина. Дольше смысла нет: полоса не срочная, ответ придёт в другой раз. */
const ТАЙМАУТ_МАГАЗИНА_МС = 8_000;

const КЛЮЧ_ПАМЯТИ_МАГАЗИНА = 'bark.store-version';

/**
 * нет — обновления не найдено либо оно ещё качается в тишине.
 * готово — файл на диске, можно ставить: только в этом состоянии человек видит полосу.
 * ставится — нажал «Обновить», идёт установка и перезапуск.
 * в-магазине — на планшете: магазин предлагает версию выше своей, ставит её сам магазин.
 */
export type ЭтапОбновления = 'нет' | 'готово' | 'ставится' | 'в-магазине';

interface СостояниеОбновления {
  этап: ЭтапОбновления;
  /** Номер готовой версии, он же текст на полосе. */
  версия: string | null;
  /**
   * Что нового в ней, текстом выпуска: `notes` манифеста на компьютере, `releaseNotes` магазина
   * на планшете. Пусто — выпуск ничего не сказал (так было до 1.0.24). Разбирает его окно
   * «Что нового» (`shared/desktop/что-нового.ts`), здесь он хранится как пришёл.
   */
  заметки: string | null;
  /** Момент, до которого полосу не показываем. Ноль — показываем сразу. */
  отложеноДо: number;
}

export const useОбновление = create<
  СостояниеОбновления & {
    объявитьГотовым(версия: string, заметки?: string | null): void;
    объявитьВМагазине(версия: string, отложеноДо?: number, заметки?: string | null): void;
    отложить(): void;
    объявитьУстановку(): void;
  }
>((set, get) => ({
  этап: 'нет',
  версия: null,
  заметки: null,
  отложеноДо: 0,
  объявитьГотовым: (версия, заметки = null) => set({ этап: 'готово', версия, заметки }),
  объявитьВМагазине: (версия, отложеноДо = 0, заметки = null) =>
    set({ этап: 'в-магазине', версия, отложеноДо, заметки }),
  отложить: () => {
    // На планшете «Позже» уходит на сутки и переживает перезапуск: человек, закрывший полосу,
    // не должен видеть её снова при каждом открытии приложения. На компьютере прежние два часа.
    const вМагазине = get().этап === 'в-магазине';
    const до = Date.now() + (вМагазине ? ОТСРОЧКА_МАГАЗИНА_МС : ОТСРОЧКА_МС);
    set({ отложеноДо: до });
    if (вМагазине) {
      запомнитьОтсрочкуМагазина(до);
    }
  },
  объявитьУстановку: () => set({ этап: 'ставится' }),
}));

// Найденное обновление держим здесь, а не в состоянии: это объект с открытым соединением,
// класть такое в хранилище состояния нельзя — оно должно оставаться сравнимым и печатаемым.
let готовоеОбновление: { install(): Promise<void> } | null = null;

/**
 * Один круг проверки: спросить, скачать, объявить готовым.
 *
 * Ничего не делает в браузере и молчит при любой ошибке — см. шапку файла.
 * Возвращает `true`, если после этого круга есть что ставить: этим пользуются тесты.
 */
export async function проверитьОбновление(): Promise<boolean> {
  if (готовоеОбновление !== null) {
    // Уже скачано и ждёт нажатия. Второй раз качать то же самое незачем.
    return true;
  }
  if (!(await десктоп())) {
    return false;
  }
  try {
    const { check } = await import('@tauri-apps/plugin-updater');
    const обновление = await check();
    if (обновление === null) {
      return false;
    }
    // Скачивание отделено от установки намеренно. Иначе кнопка «Обновить» означала бы
    // «подожди, пока приедет шесть мегабайт», и на объектовом канале это минуты ожидания
    // после нажатия. Так к моменту нажатия файл уже лежит на диске.
    await обновление.download();
    готовоеОбновление = обновление;
    // Текст изменений приходит тем же ответом, что и сам файл (`notes` манифеста). Отдельным
    // запросом его не берём: второй запрос мог бы разойтись с первым версией.
    useОбновление.getState().объявитьГотовым(обновление.version, обновление.body ?? null);
    return true;
  } catch {
    // Сеть, сервер, испорченный манифест — всё это молчание до следующего круга.
    return false;
  }
}

/**
 * Поставить скачанное и перезапустить окно.
 *
 * На Windows установщик закрывает программу сам, на macOS — нет, поэтому перезапуск
 * вызывается явно. Лишним он не будет: до него дело дойдёт только там, где программа ещё жива.
 */
export async function установитьОбновление(): Promise<void> {
  if (готовоеОбновление === null) {
    return;
  }
  useОбновление.getState().объявитьУстановку();
  try {
    await готовоеОбновление.install();
    const { relaunch } = await import('@tauri-apps/plugin-process');
    await relaunch();
  } catch {
    // Установка не пошла — возвращаем полосу, чтобы кнопка осталась нажимаемой. Молчим по
    // той же причине: человеку тут нечего делать, кроме как нажать ещё раз.
    useОбновление.setState({ этап: 'готово' });
  }
}

// ── Планшет: версия в App Store ──────────────────────────────────────────────────────────

/** Что помним про магазин между запусками: чтобы не спрашивать чаще раза в двенадцать часов. */
interface ПамятьМагазина {
  /** Когда магазин отвечал в последний раз, мс. */
  проверено: number;
  /** Версия в магазине; пусто — магазин ответил, но версии в ответе не нашлось. */
  версия: string | null;
  /** Минимальная версия системы, которую требует версия в магазине. */
  минимальнаяСистема: string | null;
  /** «Что нового» этой версии со страницы магазина. Пусто — магазин не сказал. */
  заметки: string | null;
  /** Момент, до которого полосу не показываем (нажали «Позже»). */
  отложеноДо: number;
}

function прочитатьПамятьМагазина(): ПамятьМагазина | null {
  try {
    const сырое = window.localStorage.getItem(КЛЮЧ_ПАМЯТИ_МАГАЗИНА);
    if (сырое === null) {
      return null;
    }
    const данные: unknown = JSON.parse(сырое);
    if (typeof данные !== 'object' || данные === null) {
      return null;
    }
    const запись = данные as Record<string, unknown>;
    return {
      проверено: typeof запись.проверено === 'number' ? запись.проверено : 0,
      версия: typeof запись.версия === 'string' ? запись.версия : null,
      минимальнаяСистема:
        typeof запись.минимальнаяСистема === 'string' ? запись.минимальнаяСистема : null,
      // Память, записанная версией до 1.0.24, этого поля не знает: тогда текста просто нет до
      // следующего вопроса магазину, а не битая память.
      заметки: typeof запись.заметки === 'string' ? запись.заметки : null,
      отложеноДо: typeof запись.отложеноДо === 'number' ? запись.отложеноДо : 0,
    };
  } catch {
    return null;
  }
}

function записатьПамятьМагазина(память: ПамятьМагазина): void {
  try {
    window.localStorage.setItem(КЛЮЧ_ПАМЯТИ_МАГАЗИНА, JSON.stringify(память));
  } catch {
    // Память закрыта или полна: тогда спросим магазин в следующий раз, вреда нет.
  }
}

function запомнитьОтсрочкуМагазина(до: number): void {
  const память = прочитатьПамятьМагазина();
  if (память !== null) {
    записатьПамятьМагазина({ ...память, отложеноДо: до });
  }
}

/**
 * Сравнение номеров версий по числам: `1.0.10` новее `1.0.9`, а `1.0` равна `1.0.0`.
 * Не число в части номера считается нулём: непонятное не должно давать «новая версия».
 */
export function сравнитьВерсии(а: string, б: string): number {
  const части = (версия: string) => версия.split('.').map((часть) => parseInt(часть, 10) || 0);
  const x = части(а);
  const y = части(б);
  for (let i = 0; i < Math.max(x.length, y.length); i += 1) {
    const разница = (x[i] ?? 0) - (y[i] ?? 0);
    if (разница !== 0) {
      return разница > 0 ? 1 : -1;
    }
  }
  return 0;
}

/** Версия системы планшета из строки браузера; `null`, когда не разобрать. */
function версияСистемы(): string | null {
  const совпало = /CPU (?:iPhone )?OS (\d+)[_.](\d+)(?:[_.](\d+))?/.exec(navigator.userAgent);
  return совпало === null ? null : [совпало[1], совпало[2], совпало[3] ?? '0'].join('.');
}

/**
 * Потянет ли планшет версию из магазина. Если систему не разобрать, считаем, что потянет:
 * лучше показать полосу, чем промолчать, а страница магазина сама скажет, чего не хватает.
 */
function планшетТянет(минимальнаяСистема: string | null): boolean {
  const своя = версияСистемы();
  if (минимальнаяСистема === null || своя === null) {
    return true;
  }
  return сравнитьВерсии(своя, минимальнаяСистема) >= 0;
}

/**
 * Ответ магазина по одной витрине: версия, требование к системе и что в ней нового. `null` —
 * ничего полезного.
 */
async function спроситьМагазин(): Promise<{
  версия: string;
  минимальнаяСистема: string | null;
  заметки: string | null;
} | null> {
  for (const витрина of ВИТРИНЫ_МАГАЗИНА) {
    const управление = new AbortController();
    const таймер = setTimeout(() => управление.abort(), ТАЙМАУТ_МАГАЗИНА_МС);
    try {
      const ответ = await fetch(
        `https://itunes.apple.com/lookup?id=${НОМЕР_В_МАГАЗИНЕ}&country=${витрина}`,
        { signal: управление.signal },
      );
      if (!ответ.ok) {
        continue;
      }
      const тело: unknown = await ответ.json();
      const результаты = (тело as { results?: unknown }).results;
      if (!Array.isArray(результаты) || результаты.length === 0) {
        // В этой витрине приложения нет (или оно не отдано): пробуем следующую.
        continue;
      }
      const первый = результаты[0] as {
        version?: unknown;
        minimumOsVersion?: unknown;
        releaseNotes?: unknown;
      };
      if (typeof первый.version === 'string' && первый.version !== '') {
        return {
          версия: первый.version,
          минимальнаяСистема:
            typeof первый.minimumOsVersion === 'string' ? первый.minimumOsVersion : null,
          // «Что нового» той версии, что сейчас в магазине: ровно её и предлагает полоса.
          заметки: typeof первый.releaseNotes === 'string' ? первый.releaseNotes : null,
        };
      }
    } catch {
      // Сеть, время ожидания, не тот ответ: молчим и пробуем следующую витрину.
    } finally {
      clearTimeout(таймер);
    }
  }
  return null;
}

/**
 * Один круг проверки магазина на планшете: спросить (не чаще раза в двенадцать часов),
 * сравнить с собственной версией и объявить, если магазин впереди.
 *
 * Не на планшете не делает ничего. Молчит при любой ошибке. Ответ магазина помнит между
 * запусками: перезапуск приложения не должен ни прятать полосу, ни засыпать магазин запросами.
 * Возвращает `true`, если полоса объявлена: этим пользуются тесты.
 */
export async function проверитьВерсиюВМагазине(): Promise<boolean> {
  if ((await платформа()) !== 'ipad') {
    return false;
  }
  try {
    const сейчас = Date.now();
    let память = прочитатьПамятьМагазина();
    const свежая =
      память !== null &&
      память.проверено <= сейчас &&
      сейчас - память.проверено < ПЕРИОД_ПРОВЕРКИ_МАГАЗИНА_МС;
    if (!свежая) {
      const ответ = await спроситьМагазин();
      if (ответ !== null) {
        память = {
          проверено: сейчас,
          версия: ответ.версия,
          минимальнаяСистема: ответ.минимальнаяСистема,
          заметки: ответ.заметки,
          отложеноДо: память?.отложеноДо ?? 0,
        };
        записатьПамятьМагазина(память);
      }
    }
    if (память === null || память.версия === null) {
      return false;
    }
    const { getVersion } = await import('@tauri-apps/api/app');
    const своя = await getVersion();
    if (сравнитьВерсии(память.версия, своя) <= 0 || !планшетТянет(память.минимальнаяСистема)) {
      return false;
    }
    useОбновление.getState().объявитьВМагазине(память.версия, память.отложеноДо, память.заметки);
    return true;
  } catch {
    return false;
  }
}

/** Открыть страницу приложения в магазине. Сторож переходов плагина откроет её снаружи. */
export function открытьМагазин(): void {
  window.location.assign(ССЫЛКА_НА_МАГАЗИН);
}

/**
 * Запустить слежение. Возвращает функцию остановки — её зовёт `useEffect` при размонтировании.
 *
 * Таймеры именно такие: пауза перед первым запросом и ровный шаг дальше. Проверка при каждом
 * возврате окна в фокус выглядит заманчиво, но у финансового директора окно уходит и
 * возвращается десятки раз за утро, и это был бы поток запросов вместо одного раза в пять минут.
 */
export function следитьЗаОбновлениями(): () => void {
  let остановлено = false;
  let повтор: ReturnType<typeof setInterval> | null = null;

  const первый = setTimeout(() => {
    if (остановлено) {
      return;
    }
    void проверитьОбновление();
    void проверитьВерсиюВМагазине();
    повтор = setInterval(() => {
      void проверитьОбновление();
      // На планшете спрашивает не чаще раза в двенадцать часов (сама), не на планшете молчит.
      void проверитьВерсиюВМагазине();
    }, ПЕРИОД_ПРОВЕРКИ_МС);
  }, ПАУЗА_ПЕРЕД_ПЕРВОЙ_ПРОВЕРКОЙ_МС);

  return () => {
    остановлено = true;
    clearTimeout(первый);
    if (повтор !== null) {
      clearInterval(повтор);
    }
  };
}

/** Только для тестов: вернуть модуль в исходное состояние. */
export function забытьОбновлениеДляТестов(): void {
  готовоеОбновление = null;
  useОбновление.setState({ этап: 'нет', версия: null, заметки: null, отложеноДо: 0 });
  try {
    window.localStorage.removeItem(КЛЮЧ_ПАМЯТИ_МАГАЗИНА);
  } catch {
    // Память закрыта: забывать нечего.
  }
}
